'use client';
import { Fragment, useRef } from 'react';
import { m, useInView } from 'framer-motion';
import React from 'react';
import { PortableText } from '@portabletext/react';
import { components } from '@/components/sections/PortableTextComponents';
import { PortableTextBlock } from 'sanity';
import { fadeUp } from '@/assets/framer/animations';
import Image from 'next/image';
import { CheckCircle } from '@phosphor-icons/react';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      type: 'spring',
    },
  },
};

const block = {
  hidden: { opacity: 0, y: 100 },
  show: { opacity: 1, y: 0 },
};

export default function WhatsInTheBox(props: {
  data: {
    sectionId?: string;
    heading?: PortableTextBlock;
    subheading?: PortableTextBlock;
    subheadingTwo?: PortableTextBlock;
    features: string[];
    image: {
      url: string;
      alt: string;
      blurDataUrl: string;
    };
    displayOptions: {
      hidePatterns?: boolean;
    };
  };
}) {
  const section = useRef(null);
  const sectionId = props.data?.sectionId;
  const heading = props.data?.heading;
  const subheading = props.data?.subheading;
  const subheadingTwo = props.data?.subheadingTwo;
  const features = props.data?.features;
  const image = props.data?.image;
  const displayOptions = props.data?.displayOptions;
  const isInView = useInView(section, { amount: 0.4, once: true });

  return (
    <>
      <section
        id={sectionId}
        ref={section}
        className='section-padding bg-gradient-charcoal relative z-40 mb-10 scroll-m-16 bg-gradient-to-b md:mb-0'
      >
        {/* Top Divider wave */}
        <div className='absolute left-0 top-0 h-max w-full -translate-y-[calc(100%_-_1px)] transform overflow-hidden'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='w-[200%] md:w-full [&_path]:fill-charcoal-900'
            width='1464'
            height='34'
            viewBox='0 0 1464 34'
            fill='none'
            preserveAspectRatio='none'
          >
            <path
              d='M0 34H1464V13.9567C1462.14 14.0919 1459.76 14.2755 1456.86 14.4993L1456.79 14.5044C1400.39 18.8525 1148.67 38.2577 731.531 12.6924C315.789 -12.7874 65.0374 7.19974 7.26685 11.8046C4.35205 12.0369 1.92871 12.23 0 12.3763V34Z'
              fill='none'
            />
          </svg>
        </div>

        <div className='container-padding container relative'>
          <div className='items-center md:flex md:gap-10'>
            {/* Column 1 */}
            <div
              className={`space-y-10 ${subheadingTwo ? 'w-full' : 'lg:w-3/5'}`}
            >
              <div className='flex'>
                <div
                  className={`${!image || !image.url ? 'md:w-full' : 'md:w-2/3'} space-y-5 sm:max-w-[unset] lg:w-full`}
                >
                  <div className='flex gap-5'>
                    {heading && (
                      <m.h2
                        initial='hidden'
                        animate={isInView ? 'visible' : 'hidden'}
                        variants={fadeUp}
                        className={`heading-xl relative z-10 ${features && features.length && 'max-w-[150px]'} font-display font-bold leading-[1] tracking-tighter text-yellow-150 sm:max-w-[550px] md:w-full md:leading-[0.95] [&_strong]:text-yellow`}
                      >
                        <PortableText
                          components={components}
                          value={heading}
                        ></PortableText>
                      </m.h2>
                    )}
                    {/* Mobile image  */}
                    {features && features.length && (
                      <div className='w-[120px] xxs:w-[200px]'>
                        <div className='relative block md:hidden'>
                          {image && image.url && (
                            <Image
                              className='absolute z-10 w-full max-w-[120px] -translate-y-[50%] sm:max-w-[200px] xxs:max-w-[160px] xs:max-w-[180px]'
                              src={image.url}
                              alt={image.alt ?? ''}
                              placeholder='blur'
                              blurDataURL={image.blurDataUrl}
                              width={480}
                              height={400}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div
                    className={
                      subheadingTwo &&
                      'flex flex-col gap-10 md:flex-row md:gap-32'
                    }
                  >
                    {subheading && (
                      <m.div
                        initial='hidden'
                        animate={isInView ? 'visible' : 'hidden'}
                        variants={fadeUp}
                        transition={{ delay: 0.2 }}
                        className='relative space-y-5 text-base text-white sm:text-lg md:max-w-[80%] md:text-2xl'
                      >
                        <PortableText value={subheading}></PortableText>
                      </m.div>
                    )}
                    {(!image || !image.url) && subheadingTwo && (
                      <m.div
                        initial='hidden'
                        animate={isInView ? 'visible' : 'hidden'}
                        variants={fadeUp}
                        transition={{ delay: 0.2 }}
                        className='relative space-y-5 text-base text-white sm:text-lg md:max-w-[80%] md:text-2xl'
                      >
                        <PortableText value={subheadingTwo}></PortableText>
                      </m.div>
                    )}
                  </div>
                </div>

                {image && image.url && (
                  <div className='relative hidden md:block md:w-1/3 lg:hidden'>
                    <Image
                      className='relative z-10 w-full max-w-[300px] object-contain'
                      src={image.url}
                      alt={image.alt ?? ''}
                      placeholder='blur'
                      blurDataURL={image.blurDataUrl}
                      width={480}
                      height={400}
                    />

                    {/* Pattern */}
                    {!displayOptions?.hidePatterns && (
                      <div
                        className={`absolute -bottom-4 left-0 ${!image?.url ? 'top-0 lg:-bottom-4' : '-bottom-4'}`}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='237'
                          height='156'
                          viewBox='0 0 237 156'
                          fill='none'
                          className={`relative [&_path]:fill-charcoal-700 ${!image?.url ? 'h-[50%] w-[80%] xl:w-[50%]' : 'h-[50%] w-[50%]'}`}
                        >
                          <path
                            d='M159.184 66.3952C159.662 66.3952 160.044 66.0127 160.044 65.5346V53.3715C160.044 53.3715 160.005 53.3333 160.005 53.295H172.023V65.2669C172.023 65.745 172.405 66.1466 172.902 66.1466H184.671V77.6403C184.671 78.1184 185.073 78.52 185.55 78.52H196.938V89.6695H185.55C185.073 89.6695 184.671 90.052 184.671 90.5493V102.043H173.093C172.615 102.043 172.214 102.425 172.214 102.923V115.067C172.214 115.067 172.252 115.124 172.252 115.143H160.292C160.349 115.028 160.426 114.933 160.426 114.818V102.655C160.426 102.177 160.044 101.775 159.566 101.775H148.007V90.358C148.007 89.8799 147.625 89.4974 147.147 89.4974H135.416V78.8452C135.416 78.8452 135.359 78.7304 135.339 78.673H146.86C147.338 78.673 147.72 78.2905 147.72 77.8124V66.3952H159.203H159.184ZM109.68 64.7696C109.202 64.7696 108.82 65.1521 108.82 65.6302V77.7933C108.82 77.7933 108.878 77.9081 108.897 77.9654H96.8789C96.4012 77.9654 96 78.367 96 78.8452V90.9891C96 91.4672 96.3821 91.8688 96.8789 91.8688H109.183V102.521C109.183 102.999 109.565 103.382 110.043 103.382H121.373V114.799C121.373 115.277 121.755 115.66 122.233 115.66H134.25C134.136 115.832 134.002 116.023 134.002 116.233V127.899C134.002 128.511 134.499 129.027 135.11 129.027H146.478C146.478 129.027 146.44 129.066 146.44 129.104V141.267C146.44 141.745 146.822 142.147 147.319 142.147H159.356V154.654C159.356 154.941 159.585 155.17 159.872 155.17H172.73C173.017 155.17 173.246 154.941 173.246 154.654V142.147H184.901C185.378 142.147 185.78 141.745 185.78 141.267V129.104C185.78 129.104 185.76 129.066 185.76 129.027H197.625C198.237 129.027 198.734 128.53 198.734 127.899V116.233C198.734 116.099 198.638 116.023 198.6 115.908H210.713C211.191 115.908 211.592 115.526 211.592 115.028V103.535H223.38C223.858 103.535 224.259 103.133 224.259 102.655V91.1804H235.417C235.895 91.1804 236.296 90.7979 236.296 90.3006V78.1376C236.296 77.6594 235.895 77.2578 235.417 77.2578H223.896V65.439C223.896 64.9609 223.514 64.5593 223.017 64.5593H211.229V53.0464C211.229 52.5683 210.847 52.1858 210.35 52.1858H199.058C199.058 52.1858 199.097 52.1284 199.097 52.1093V40.4626C199.097 39.8506 198.6 39.3342 197.969 39.3342H185.321C185.378 39.2195 185.455 39.143 185.455 39.0091V26.846C185.455 26.3679 185.054 25.9663 184.576 25.9663H172.883V13.5164C172.883 13.2295 172.654 13 172.367 13H159.509C159.222 13 158.993 13.2295 158.993 13.5164V26.0237H146.956C146.478 26.0237 146.077 26.4062 146.077 26.9034V39.0665C146.077 39.2004 146.173 39.2769 146.211 39.3916H134.613C133.983 39.3916 133.486 39.8888 133.486 40.5199V52.1667C133.486 52.3005 133.582 52.377 133.62 52.4918H121.831C121.354 52.4918 120.953 52.8743 120.953 53.3524V64.7696H109.623H109.68Z'
                            fill='currentColor'
                          />
                          <path
                            d='M58.2136 141.474H46.043V130.056H58.2136V141.474ZM32.5159 143.233H45.1641V154.975C45.1641 155.262 45.3934 155.492 45.68 155.492H58.653C58.9396 155.492 59.1689 155.262 59.1689 154.975V142.296H71.6834C71.97 142.296 72.1992 142.066 72.1992 141.78V128.775C72.1992 128.488 71.97 128.259 71.6834 128.259H59.0351V116.516C59.0351 116.229 58.8059 116 58.5193 116H45.5462C45.2596 116 45.0304 116.229 45.0304 116.516V129.196H32.5159C32.2293 129.196 32 129.425 32 129.712V142.698C32 142.984 32.2293 143.214 32.5159 143.214'
                            fill='currentColor'
                          />
                          <path
                            d='M26.1563 26.6976C26.4428 26.6976 26.6721 26.4681 26.6721 26.1812V14.0373H39.2248V26.1812C39.2248 26.4681 39.4541 26.6976 39.7407 26.6976H51.4336V39.2049H39.3013C39.0147 39.2049 38.7854 39.4344 38.7854 39.7213V52.1712H26.9778V39.7787C26.9778 39.4918 26.7485 39.2623 26.462 39.2623H14.0048V26.6976H26.118H26.1563ZM0.515865 40.0081H12.973V52.7832C12.973 53.0701 13.2023 53.2996 13.4889 53.2996H26.1563V65.6922C26.1563 65.979 26.3855 66.2085 26.6721 66.2085H39.6643C39.9509 66.2085 40.1801 65.979 40.1801 65.6922V53.2231H52.3125C52.5991 53.2231 52.8284 52.9936 52.8284 52.7067V39.9316H64.9607C65.2473 39.9316 65.4766 39.7022 65.4766 39.4153V26.4299C65.4766 26.143 65.2473 25.9135 64.9607 25.9135H53.2678V13.1958C53.2678 12.9089 53.0385 12.6794 52.7519 12.6794H39.9891V0.516358C39.9891 0.229492 39.7598 0 39.4732 0H26.5002C26.2136 0 25.9843 0.229492 25.9843 0.516358V12.6412H13.2214C12.9348 12.6412 12.7056 12.8707 12.7056 13.1576V25.9326H0.515865C0.229273 25.9326 0 26.1621 0 26.449V39.4344C0 39.7213 0.229273 39.9508 0.515865 39.9508'
                            fill='currentColor'
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tablet and small laptops image  */}
              <m.div
                initial='hidden'
                variants={container}
                animate={isInView ? 'show' : 'hidden'}
                className='grid gap-3 md:grid-cols-2'
              >
                {features &&
                  features.length > 0 &&
                  features.map((feature, index) => (
                    <Fragment key={`feature-${index}`}>
                      <m.div
                        variants={block}
                        className='flex items-center gap-2 rounded-lg bg-charcoal-800/50 px-2.5 py-2.5 sm:gap-3'
                      >
                        <span className='flex-shrink-0'>
                          <CheckCircle
                            weight='fill'
                            className='h-8 w-8 text-yellow'
                          />
                        </span>
                        <span className='font-medium text-white'>
                          {feature}
                        </span>
                      </m.div>
                    </Fragment>
                  ))}
              </m.div>
            </div>

            {/* Column 2 */}
            {image && image.url && (
              <div
                className={`${features && features.length && 'hidden'} min-h-[200px] self-start lg:block lg:w-2/5`}
              >
                <div className='relative top-0'>
                  <Image
                    className='relative z-10'
                    src={image.url}
                    alt={image.alt ?? ''}
                    placeholder='blur'
                    blurDataURL={image.blurDataUrl}
                    width={480}
                    height={400}
                  />

                  {/* Pattern */}
                  {!displayOptions?.hidePatterns && (
                    <div
                      className={`absolute -bottom-4 left-0 ${!image?.url ? 'top-0 lg:-bottom-4' : '-bottom-4'}`}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='237'
                        height='156'
                        viewBox='0 0 237 156'
                        fill='none'
                        className='[&_path]:fill-charcoal-700'
                      >
                        <path
                          d='M159.184 66.3952C159.662 66.3952 160.044 66.0127 160.044 65.5346V53.3715C160.044 53.3715 160.005 53.3333 160.005 53.295H172.023V65.2669C172.023 65.745 172.405 66.1466 172.902 66.1466H184.671V77.6403C184.671 78.1184 185.073 78.52 185.55 78.52H196.938V89.6695H185.55C185.073 89.6695 184.671 90.052 184.671 90.5493V102.043H173.093C172.615 102.043 172.214 102.425 172.214 102.923V115.067C172.214 115.067 172.252 115.124 172.252 115.143H160.292C160.349 115.028 160.426 114.933 160.426 114.818V102.655C160.426 102.177 160.044 101.775 159.566 101.775H148.007V90.358C148.007 89.8799 147.625 89.4974 147.147 89.4974H135.416V78.8452C135.416 78.8452 135.359 78.7304 135.339 78.673H146.86C147.338 78.673 147.72 78.2905 147.72 77.8124V66.3952H159.203H159.184ZM109.68 64.7696C109.202 64.7696 108.82 65.1521 108.82 65.6302V77.7933C108.82 77.7933 108.878 77.9081 108.897 77.9654H96.8789C96.4012 77.9654 96 78.367 96 78.8452V90.9891C96 91.4672 96.3821 91.8688 96.8789 91.8688H109.183V102.521C109.183 102.999 109.565 103.382 110.043 103.382H121.373V114.799C121.373 115.277 121.755 115.66 122.233 115.66H134.25C134.136 115.832 134.002 116.023 134.002 116.233V127.899C134.002 128.511 134.499 129.027 135.11 129.027H146.478C146.478 129.027 146.44 129.066 146.44 129.104V141.267C146.44 141.745 146.822 142.147 147.319 142.147H159.356V154.654C159.356 154.941 159.585 155.17 159.872 155.17H172.73C173.017 155.17 173.246 154.941 173.246 154.654V142.147H184.901C185.378 142.147 185.78 141.745 185.78 141.267V129.104C185.78 129.104 185.76 129.066 185.76 129.027H197.625C198.237 129.027 198.734 128.53 198.734 127.899V116.233C198.734 116.099 198.638 116.023 198.6 115.908H210.713C211.191 115.908 211.592 115.526 211.592 115.028V103.535H223.38C223.858 103.535 224.259 103.133 224.259 102.655V91.1804H235.417C235.895 91.1804 236.296 90.7979 236.296 90.3006V78.1376C236.296 77.6594 235.895 77.2578 235.417 77.2578H223.896V65.439C223.896 64.9609 223.514 64.5593 223.017 64.5593H211.229V53.0464C211.229 52.5683 210.847 52.1858 210.35 52.1858H199.058C199.058 52.1858 199.097 52.1284 199.097 52.1093V40.4626C199.097 39.8506 198.6 39.3342 197.969 39.3342H185.321C185.378 39.2195 185.455 39.143 185.455 39.0091V26.846C185.455 26.3679 185.054 25.9663 184.576 25.9663H172.883V13.5164C172.883 13.2295 172.654 13 172.367 13H159.509C159.222 13 158.993 13.2295 158.993 13.5164V26.0237H146.956C146.478 26.0237 146.077 26.4062 146.077 26.9034V39.0665C146.077 39.2004 146.173 39.2769 146.211 39.3916H134.613C133.983 39.3916 133.486 39.8888 133.486 40.5199V52.1667C133.486 52.3005 133.582 52.377 133.62 52.4918H121.831C121.354 52.4918 120.953 52.8743 120.953 53.3524V64.7696H109.623H109.68Z'
                          fill='currentColor'
                        />
                        <path
                          d='M58.2136 141.474H46.043V130.056H58.2136V141.474ZM32.5159 143.233H45.1641V154.975C45.1641 155.262 45.3934 155.492 45.68 155.492H58.653C58.9396 155.492 59.1689 155.262 59.1689 154.975V142.296H71.6834C71.97 142.296 72.1992 142.066 72.1992 141.78V128.775C72.1992 128.488 71.97 128.259 71.6834 128.259H59.0351V116.516C59.0351 116.229 58.8059 116 58.5193 116H45.5462C45.2596 116 45.0304 116.229 45.0304 116.516V129.196H32.5159C32.2293 129.196 32 129.425 32 129.712V142.698C32 142.984 32.2293 143.214 32.5159 143.214'
                          fill='currentColor'
                        />
                        <path
                          d='M26.1563 26.6976C26.4428 26.6976 26.6721 26.4681 26.6721 26.1812V14.0373H39.2248V26.1812C39.2248 26.4681 39.4541 26.6976 39.7407 26.6976H51.4336V39.2049H39.3013C39.0147 39.2049 38.7854 39.4344 38.7854 39.7213V52.1712H26.9778V39.7787C26.9778 39.4918 26.7485 39.2623 26.462 39.2623H14.0048V26.6976H26.118H26.1563ZM0.515865 40.0081H12.973V52.7832C12.973 53.0701 13.2023 53.2996 13.4889 53.2996H26.1563V65.6922C26.1563 65.979 26.3855 66.2085 26.6721 66.2085H39.6643C39.9509 66.2085 40.1801 65.979 40.1801 65.6922V53.2231H52.3125C52.5991 53.2231 52.8284 52.9936 52.8284 52.7067V39.9316H64.9607C65.2473 39.9316 65.4766 39.7022 65.4766 39.4153V26.4299C65.4766 26.143 65.2473 25.9135 64.9607 25.9135H53.2678V13.1958C53.2678 12.9089 53.0385 12.6794 52.7519 12.6794H39.9891V0.516358C39.9891 0.229492 39.7598 0 39.4732 0H26.5002C26.2136 0 25.9843 0.229492 25.9843 0.516358V12.6412H13.2214C12.9348 12.6412 12.7056 12.8707 12.7056 13.1576V25.9326H0.515865C0.229273 25.9326 0 26.1621 0 26.449V39.4344C0 39.7213 0.229273 39.9508 0.515865 39.9508'
                          fill='currentColor'
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Divider wave */}
        <div className='absolute bottom-0 left-0 h-max w-full translate-y-[calc(100%_-_1px)] transform overflow-hidden'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='w-[200%] md:w-full [&_path]:fill-charcoal'
            width='1464'
            height='26'
            viewBox='0 0 1464 26'
            fill='none'
            preserveAspectRatio='none'
          >
            <path
              d='M0 0V13.4014C71.6641 8.27045 311.629 -4.51068 731.531 15.2506C1153.28 35.099 1393.51 22.1175 1464 17.0324V0H0Z'
              fill='none'
            />
          </svg>
        </div>
      </section>
    </>
  );
}
